import { onBeforeUnmount, onMounted, useAttrs, watch, computed } from 'vue'
import { usePipe, type EditorCommandHandler } from './usePipe'

/**
 * Register editor-only command handlers for this component instance. Paired
 * with the `<EditorCommandButton>` field editor, which sends
 * `EXTERNAL_EDITOR_COMMAND` messages into the preview iframe from the field
 * editor panel.
 *
 * Use this for authoring-time operations that should NOT appear in the user's
 * event-wiring dropdown — preview an animation, jump a TabView to a specific
 * tab, seek a video to t=X, flip a light theme for visual check, etc. If the
 * operation is something end-users of the built pipe should be able to wire
 * to (e.g. a Button.onClick fires it), declare it as a processor via
 * `useComponentProcessors` instead — or alongside, pointing at the same
 * implementation.
 *
 * The node is keyed by its raw `data-id` (set by RenderComponents); unlike
 * component processors, no SharedNodeRef ancestry is composed. The editor
 * addresses concrete authored nodes — shared-template internals aren't
 * individually selectable from the field editor.
 *
 * Usage:
 * ```ts
 * useEditorCommands({
 *   previewAnimation: () => startAnimation(),
 *   switchToTab: ({ config }) => setCurrentTab(String(config.tabId)),
 * })
 * ```
 */
export function useEditorCommands(handlers: Record<string, EditorCommandHandler>) {
  const pipe = usePipe()
  const attrs = useAttrs()

  const nodeId = computed<string | null>(() => (attrs['data-id'] as string | undefined) ?? null)

  let lastKey: string | null = null

  onMounted(() => {
    const key = nodeId.value
    if (!key) {
      console.warn(
        '[useEditorCommands] data-id attr not found on component — cannot ' +
          'register editor commands. Is this component rendered via RenderComponents?',
      )
      return
    }
    pipe.registerEditorCommands(key, handlers)
    lastKey = key
  })

  watch(nodeId, (newKey) => {
    if (newKey === lastKey) return
    if (lastKey) pipe.unregisterEditorCommands(lastKey, handlers)
    if (newKey) pipe.registerEditorCommands(newKey, handlers)
    lastKey = newKey
  })

  onBeforeUnmount(() => {
    if (lastKey) pipe.unregisterEditorCommands(lastKey, handlers)
  })
}
